
import { TranslateResult } from 'vue-i18n/types'
import {
  DesktopIconsItem as HeaderDesktopIconsItem,
  Icon,
  MALL_ICONS,
  RoundedButton,
} from '@mg/micro/vue2'
import { WishlistDropdown } from '@mg/micro/vue2/WishlistDropdown'
import type {
  WishlistDropdownData,
  WishlistDropdownItem,
} from '@mg/micro/vue2/WishlistDropdown'
import { Component, Emit, Prop, Vue } from 'vue-property-decorator'
import cloneDeep from 'lodash/cloneDeep'
import { getImageUrl, ImageSize } from '@mg/web-components/utils/image'
import { snakeCasedAvailabilityKey } from '~/utils/domain/Availability'
import {
  GET_WISH_LISTS,
  MUTATION_DELETE_WISH_LIST_ITEM,
} from '~/graphql/gql/wishList'
import { GQL_LOCAL_MUTATION_ADD_TOC_CART } from '~/graphql/gqlLocal/localCart'
import {
  GQLWishList,
  GQLWishListItem,
  GQLWishListItemIdInput,
} from '~/graphql/__generated__/interfaces'
import { ApolloHelpers } from '~/node_modules/@nuxtjs/apollo'
import { MutationOptions } from '~/node_modules/apollo-client'
import {
  GQLLocalCartAddInput,
  GQLLocalOpenHeaderEnum,
} from '~/graphql/__generated__/interfacesLocal'
import AppLink from '~/components/AppLink.vue'
import ROUTES from '~/config/routes'
import ApolloFetchPolicies from '~/utils/apolloFetchPolicies'

const ADD_CART_COUNT = 1

interface AvailabilityTranslations {
  [index: string]: TranslateResult
}

interface WishlistDropdownItemWithRaw extends WishlistDropdownItem {
  /** original GraphQL wishlist item, needed to resolve availability/id types on delete/buy */
  raw: GQLWishListItem
}

@Component<HeaderWishList>({
  apollo: {
    wishLists: {
      fetchPolicy: ApolloFetchPolicies.CACHE_ONLY,
      query: GET_WISH_LISTS,
    },
  },
  components: {
    AppLink,
    RoundedButton,
    HeaderDesktopIconsItem,
    Icon,
    WishlistDropdown,
  },
})
export default class HeaderWishList extends Vue {
  @Prop(Boolean) isOpenState!: boolean

  wishLists: GQLWishList[]
  error: ApolloHelpers | null = null
  isLoadingItem: Record<string, boolean> = {}
  routes = ROUTES
  MALL_ICONS = MALL_ICONS

  @Emit('changeTab')
  changeTab(tabName: null | GQLLocalOpenHeaderEnum) {
    return tabName
  }

  onWishlistMouseEnter() {
    this.open()
  }

  openToggle() {
    this.isOpenState ? this.close() : this.open()
  }

  get mainWishlist() {
    if (!this.wishLists) {
      return
    }

    return this.wishLists.find((wishLists) => wishLists.isMain)
  }

  open() {
    if (this.mainWishlist?.totalCount) {
      this.changeTab(GQLLocalOpenHeaderEnum.WishList)
    }
  }

  close() {
    this.changeTab(null)
  }

  get wishlistItems(): GQLWishListItem[] {
    const items = this.mainWishlist?.items ?? []
    return items.filter(
      (item): item is GQLWishListItem => !!item?.variant
    )
  }

  get translations() {
    return {
      error: this.$t('common.error_wish_list') as string,
      buy: this.$t('common.buy') as string,
      fullList: this.$t('common.full_list') as string,
    }
  }

  get wishlistDropdownData(): WishlistDropdownData | null {
    if (!this.mainWishlist) {
      return null
    }

    return {
      id: String(this.mainWishlist.id),
      totalCount: this.mainWishlist.totalCount ?? 0,
      items: this.wishlistItems.map((item) => this.mapItem(item)),
    }
  }

  private mapItem(item: GQLWishListItem): WishlistDropdownItemWithRaw {
    return {
      id: item.id ?? '',
      title: item.variant!.title,
      href: this.productHref(item),
      availabilityText: this.productAvailability(item) as string,
      isAvailable: item.variant!.isAvailable,
      image: getImageUrl(item.variant!.mediaId ?? '', ImageSize.ICON_LARGE),
      price: item.variant!.price,
      raw: item,
    }
  }

  get availabilityTranslations(): AvailabilityTranslations {
    return {
      avail_stock_mall: this.$t('common.avail-stock-mall'),
      avail_unavailable: this.$t('common.avail_unavailable'),
      avail_supplier: this.$t('common.avail_supplier'),
      avail_digital_content: this.$t('common.avail_digital_content'),
      avail_not_sold_separately_simple: this.$t(
        'common.avail-not-sold-separately_simple'
      ),
      avail_sold_out: this.$t('common.avail-sold-out'),
      avail_stock_partner: this.$t('common.avail-stock-partner'),
    }
  }

  productHref(item: GQLWishListItem) {
    return `/${item.product?.mainMenuUrlKey}/${item.variant?.variantUrl}`
  }

  productAvailability(item: GQLWishListItem) {
    const key = snakeCasedAvailabilityKey(item.variant?.availability?.key ?? '')
    return this.availabilityTranslations[key]
  }

  onDeleteClick(item: WishlistDropdownItemWithRaw) {
    return this.deleteProduct(this.mainWishlist!.id, item.raw.id)
  }

  onBuyClick(item: WishlistDropdownItemWithRaw) {
    return this.addToCart(this.mainWishlist!, item.raw)
  }

  async deleteProduct(
    wishListId: GQLWishList['id'],
    wishListItemId: GQLWishListItem['id']
  ) {
    await this.apolloWishList(
      {
        mutation: MUTATION_DELETE_WISH_LIST_ITEM,
        variables: {
          wishListItemId: {
            wishListId,
            wishListItemId,
          } as GQLWishListItemIdInput,
        },
      },
      wishListItemId
    )
  }

  async apolloWishList(
    mutation: MutationOptions<this>,
    wishListItemId: GQLWishListItem['id']
  ) {
    try {
      this.error = null
      this.setLoading(wishListItemId, true)

      await this.$apollo.mutate(mutation)
      this.setLoading(wishListItemId, false)
    } catch (e) {
      this.error = e
      this.setLoading(wishListItemId, false)
    }
  }

  private setLoading(
    wishListItemId: GQLWishListItem['id'],
    isLoading: boolean
  ) {
    this.isLoadingItem = {
      ...this.isLoadingItem,
      [String(wishListItemId)]: isLoading,
    }
    this.isLoadingItem = cloneDeep(this.isLoadingItem)
  }

  async addToCart(wishList: GQLWishList, wishListItem: GQLWishListItem) {
    await this.apolloWishList(
      {
        mutation: GQL_LOCAL_MUTATION_ADD_TOC_CART,
        variables: {
          addCart: {
            amount: wishListItem.amount,
            count: ADD_CART_COUNT,
            itemId: wishListItem?.product?.id,
            variantId: wishListItem?.variant?.id,
          } as GQLLocalCartAddInput,
        },
      },
      wishListItem.id
    )

    await this.deleteProduct(wishList.id, wishListItem.id)

    this.changeTab(GQLLocalOpenHeaderEnum.Cart)
  }
}
